import React, { Fragment, useEffect, useState } from "react";
import {
  EuiButton,
  EuiButtonEmpty,
  EuiButtonIcon,
  EuiCheckbox,
  EuiCheckboxGroup,
  EuiFieldText,
  EuiFlexGroup,
  EuiFlexItem,
  EuiForm,
  EuiFormRow,
  EuiFilePicker,
  EuiSpacer,
  EuiSuperSelect,
  EuiDatePicker,
  EuiTextArea,
  EuiText,
  EuiEmptyPrompt,
  htmlIdGenerator,
  EuiRadioGroup,
} from "@elastic/eui";

// import zicta_logo from "./assets/zicta-logo3.png";
import nii_logo from "./assets/nii-logo.png";
// import undp_logo from "./assets/undp-logo3.png";
import ntbc_logo from "./assets/logo-cropped.png";
import { Route, Switch, useHistory } from "react-router-dom";
import { db, storage } from "./firebase/firebase";
import { v4 } from "uuid";
import { genderOptions, provinceOptions } from "./Options";
import { applicationClass, formClass, logosClass, titleClass } from "./styles";
import { useForm } from "react-hook-form";
import { Moment } from "moment";
import { AdminLogin } from "./AdminLogin";
import { ApplicationSubmissions } from "./ApplicationSubmissions";
import { NIIHome } from "./NiiHome";

import axios from "axios";

const idPrefix = htmlIdGenerator()();

const focusAreaId = (groupIndex: number, itemIndex: number) =>
  `${idPrefix}-focus-${groupIndex}-${itemIndex}`;

const focusAreaGroups = [
  {
    category: "Agriculture",
    items: [
      "Affordable soil testing technologies",
      "Post-harvest handling and practices",
    ],
  },
  {
    category: "Fisheries and Livestock",
    items: [
      "Testing kits for microbial residuals in meat and milk",
      "Disease detection techniques/technology in cattle",
      "Aquaculture technologies",
    ],
  },
  {
    category: "Manufacturing",
    items: [
      "Low-cost processing technologies focused on small scale, sustainable and capital efficient agriculture value addition",
    ],
  },
  {
    category: "Mining",
    items: [
      "Small-scale mineral processing and sorting technologies",
      "Exploration and mineral identification technologies",
    ],
  },
];

const disabilityOptions = [
  {
    id: `${idPrefix}-disability-yes`,
    label: "Yes",
  },
  {
    id: `${idPrefix}-disability-no`,
    label: "No",
  },
];

const stageOptions = [
  {
    id: `${idPrefix}-stage-prototype`,
    label: "Prototype",
  },
  {
    id: `${idPrefix}-stage-final-product`,
    label: "Final Product",
  },
];

interface ITeamMember {
  name: string;
  expertise: string;
}

interface ICostItem {
  description: string;
  cost: string;
}

export const ApplicationForm: React.FC = () => {
  const history = useHistory();
  const [loading, setLoading] = useState<boolean>(false);
  const [gender, setGender] = useState<string>("");
  const [genderErrors, setGenderErrors] = useState<boolean>(false);
  const [province, setProvince] = useState<string>("");
  const [provinceErrors, setProvinceError] = useState(false);
  const [dateOfBirth, setdateOfBirth] = useState<Moment>();
  const [dobErrors, setDobErrors] = useState<boolean>(false);

  const [disabilityIdSelected, setDisabilityIdSelected] = useState(
    disabilityOptions[1].id
  );
  const hasDisability = disabilityIdSelected === disabilityOptions[0].id;

  const [stageIdSelected, setStageIdSelected] = useState(stageOptions[0].id);

  const [focusAreaMap, setFocusAreaMap] = useState<Record<string, boolean>>(
    {}
  );
  const [focusAreaErrors, setFocusAreaErrors] = useState<boolean>(false);

  const onFocusAreaChange = (optionId: string) => {
    setFocusAreaMap({ ...focusAreaMap, [optionId]: !focusAreaMap[optionId] });
    setFocusAreaErrors(false);
  };

  const [teamMembers, setTeamMembers] = useState<ITeamMember[]>([
    { name: "", expertise: "" },
  ]);
  const [teamErrors, setTeamErrors] = useState<boolean>(false);

  const updateTeamMember = (
    index: number,
    field: keyof ITeamMember,
    value: string
  ) => {
    const updated = [...teamMembers];
    updated[index] = { ...updated[index], [field]: value };
    setTeamMembers(updated);
    setTeamErrors(false);
  };

  const [costItems, setCostItems] = useState<ICostItem[]>([
    { description: "", cost: "" },
  ]);
  const [costErrors, setCostErrors] = useState<boolean>(false);

  const updateCostItem = (
    index: number,
    field: keyof ICostItem,
    value: string
  ) => {
    const updated = [...costItems];
    updated[index] = { ...updated[index], [field]: value };
    setCostItems(updated);
    setCostErrors(false);
  };

  const [disclaimerChecked, setDisclaimerChecked] = useState<boolean>(false);
  const [disclaimerErrors, setDisclaimerErrors] = useState<boolean>(false);

  const [files, setFiles] = useState<any>(null);
  const onChange = (fs: any) => {
    setFiles(fs);
  };

  const { register, handleSubmit, errors } = useForm();

  const onFormSubmit = async (data: any) => {
    if (!gender) {
      setGenderErrors(true);
      return;
    }

    if (!province) {
      setProvinceError(true);
      return;
    }

    if (!dateOfBirth) {
      setDobErrors(true);
      return;
    }

    const focusAreas: string[] = [];
    focusAreaGroups.forEach((group, groupIndex) =>
      group.items.forEach((item, itemIndex) => {
        if (focusAreaMap[focusAreaId(groupIndex, itemIndex)]) {
          focusAreas.push(`${group.category}: ${item}`);
        }
      })
    );
    if (focusAreas.length < 1) {
      setFocusAreaErrors(true);
      return;
    }

    const team = teamMembers.filter((member) => member.name.trim());
    if (team.length < 1) {
      setTeamErrors(true);
      return;
    }

    const costs = costItems.filter((item) => item.description.trim());
    if (costs.length < 1) {
      setCostErrors(true);
      return;
    }

    if (!disclaimerChecked) {
      setDisclaimerErrors(true);
      return;
    }

    setLoading(true);
    const id = v4();

    const projectStage = stageOptions.filter(
      (option) => option.id === stageIdSelected
    )[0].label;

    const application = {
      id,
      ...data,
      disability: hasDisability ? "Yes" : "No",
      focusAreas,
      projectStage,
      teamMembers: team,
      costItems: costs,
      province,
      gender,
      disclaimerAccepted: disclaimerChecked,
      dob: db.Timestamp.fromDate(dateOfBirth.toDate()),
      timestamp: db.Timestamp.now(),
    };

    if (files) {
      await storage()
        .ref(`/nii-applications-2022/${id}/${files[0].name}`)
        .put(files[0])
        .then((x) =>
          x.ref.getDownloadURL().then(async (url) => {
            await db()
              .collection("nii-applications-2026")
              .doc(id)
              .set({
                ...application,
                fileUrl: url,
              });
          })
        );
    } else {
      await db().collection("nii-applications-2026").doc(id).set(application);
    }

    localStorage.setItem("recipient", data.email);
    localStorage.setItem("recipientsName", data.innovatorName);

    history.push("/submitted");
  };

  const handleChange = (date: any) => {
    setdateOfBirth(date);
  };

  return (
    <div className={applicationClass}>
      <div className={titleClass}>
        <div className={logosClass}>
          <img
            src={nii_logo}
            alt="NII Logo"
            style={{ width: "auto", height: 64, marginRight: 4 }}
          />
        </div>
        <EuiText style={{ maxWidth: 500 }}>
          <h2>National Innovation Initiative </h2>
          <p>
            The call for applications is open up to 7th August, 2026 at
            23:59CAT. For more Information call, text, or Whatsapp this number
            +(260) 954 235 099
          </p>
        </EuiText>
        <EuiSpacer size="s" />
        {/* <div className={logosClass}>
          <img
            src={ntbc_logo}
            alt="NTBC Logo"
            style={{ width: "auto", height: 64, marginRight: 4 }}
          />
          <img
            src={undp_logo}
            alt="UNDP Logo"
            style={{ width: "auto", height: 64, marginRight: 4 }}
          />
        </div> */}
      </div>
      <EuiForm
        component="form"
        className={formClass}
        onSubmit={handleSubmit(onFormSubmit)}
        isInvalid={dobErrors}
      >
        <EuiText>
          <h3>Section (A): Background</h3>
        </EuiText>
        <EuiSpacer size="s" />

        <EuiFormRow
          fullWidth
          label="Name of Innovation"
          helpText="The name/title of your innovative project/solution."
        >
          <EuiFieldText
            name="title"
            inputRef={register({ required: true })}
            isInvalid={errors.title && true}
            fullWidth
          />
        </EuiFormRow>

        <EuiSpacer size="m" />
        <EuiText>
          <h4>Personal Details</h4>
        </EuiText>

        <EuiFormRow
          fullWidth
          label="Name"
          helpText="If you are in a group, add the name of the team leader."
        >
          <EuiFieldText
            name="innovatorName"
            inputRef={register({ required: true })}
            isInvalid={errors.innovatorName && true}
            fullWidth
          />
        </EuiFormRow>
        <EuiFormRow label="Gender" isInvalid={genderErrors} fullWidth>
          <EuiSuperSelect
            options={genderOptions}
            valueOfSelected={gender}
            fullWidth
            isInvalid={genderErrors}
            onChange={(value: any) => setGender(value)}
          />
        </EuiFormRow>
        <EuiFormRow label="Date of Birth" isInvalid={false} fullWidth>
          <EuiDatePicker
            isInvalid={dobErrors}
            selected={dateOfBirth}
            placeholder="Enter date of birth"
            onChange={handleChange}
            fullWidth
          />
        </EuiFormRow>
        <EuiFormRow
          label="NRC Number"
          helpText="Enter your NRC, i.e., 123456/78/9."
          fullWidth
        >
          <EuiFieldText
            name="nrcNumber"
            inputRef={register({ required: true })}
            fullWidth
          />
        </EuiFormRow>
        <EuiFormRow fullWidth>
          <EuiRadioGroup
            options={disabilityOptions}
            idSelected={disabilityIdSelected}
            onChange={(id) => setDisabilityIdSelected(id)}
            name="disability"
            legend={{
              children: <span>Are you living with a disability?</span>,
            }}
          />
        </EuiFormRow>
        {hasDisability && (
          <EuiFormRow fullWidth label="If yes, please specify">
            <EuiFieldText
              name="disabilityDetails"
              inputRef={register({ required: true })}
              isInvalid={errors.disabilityDetails && true}
              fullWidth
            />
          </EuiFormRow>
        )}
        <EuiFormRow
          label="Phone Number"
          helpText="Your primary phone number."
          fullWidth
        >
          <EuiFieldText
            name="contact"
            inputRef={register({ required: true })}
            fullWidth
          />
        </EuiFormRow>

        <EuiFormRow label="Alternative Phone Number" fullWidth>
          <EuiFieldText
            name="altContact"
            inputRef={register({ required: true })}
            fullWidth
          />
        </EuiFormRow>

        <EuiFormRow label="Email" fullWidth>
          <EuiFieldText
            name="email"
            inputRef={register({ required: true })}
            fullWidth
          />
        </EuiFormRow>

        <EuiFormRow label="Residential Address" fullWidth>
          <EuiFieldText
            name="residentialAddress"
            inputRef={register({ required: true })}
            isInvalid={errors.residentialAddress && true}
            fullWidth
          />
        </EuiFormRow>

        <EuiFormRow label="Province" isInvalid={provinceErrors} fullWidth>
          <EuiSuperSelect
            options={provinceOptions}
            valueOfSelected={province}
            isInvalid={provinceErrors}
            fullWidth
            onChange={(value: any) => setProvince(value)}
          />
        </EuiFormRow>

        <EuiSpacer size="l" />
        <EuiText>
          <h3>Section (B): Proposed Project</h3>
        </EuiText>
        <EuiSpacer size="s" />

        <EuiFormRow
          label="Focus Areas"
          helpText="Please tick the focus area(s) your innovative solution addresses."
          isInvalid={focusAreaErrors}
          error={focusAreaErrors ? ["Please tick at least one focus area."] : []}
          fullWidth
        >
          <div>
            {focusAreaGroups.map((group, groupIndex) => (
              <div key={group.category}>
                <EuiText size="s">
                  <strong>{group.category}</strong>
                </EuiText>
                <EuiCheckboxGroup
                  options={group.items.map((item, itemIndex) => ({
                    id: focusAreaId(groupIndex, itemIndex),
                    label: item,
                  }))}
                  idToSelectedMap={focusAreaMap}
                  onChange={(optionId) => onFocusAreaChange(optionId)}
                />
                <EuiSpacer size="s" />
              </div>
            ))}
          </div>
        </EuiFormRow>

        <EuiFormRow
          fullWidth
          helpText="Provide evidence e.g. photos/pictures using the supporting image upload below."
        >
          <EuiRadioGroup
            options={stageOptions}
            idSelected={stageIdSelected}
            onChange={(id) => setStageIdSelected(id)}
            name="projectStage"
            legend={{
              children: <span>Current Stage of the Project</span>,
            }}
          />
        </EuiFormRow>

        <EuiFormRow
          label="Challenge Description"
          helpText="Maximum of 200 words."
          fullWidth
        >
          <EuiTextArea
            name="challenge"
            placeholder="Clearly describe the challenge faced by the public/community that the innovation aims to address"
            inputRef={register({ required: true })}
            isInvalid={errors.challenge && true}
            fullWidth
          />
        </EuiFormRow>

        <EuiFormRow
          label="Innovativeness of the Solution"
          helpText="Maximum of 200 words."
          fullWidth
        >
          <EuiTextArea
            name="innovativeness"
            placeholder="Clearly outline how your innovation will solve the problem stated above. Please indicate if your innovation is completely new or an improvement of something already existing"
            inputRef={register({ required: true })}
            isInvalid={errors.innovativeness && true}
            fullWidth
          />
        </EuiFormRow>

        <EuiFormRow
          label="Social and Economic Impact of the Project"
          helpText="Maximum of 200 words."
          fullWidth
        >
          <EuiTextArea
            name="socialImpact"
            placeholder="Briefly highlight the benefit of the innovation and how it will uplift the living standards of the target group, demonstrate how the solution is tailored to small-scale and rural users and provide evidence of community involvement or potential for local adoption"
            inputRef={register({ required: true })}
            isInvalid={errors.socialImpact && true}
            fullWidth
          />
        </EuiFormRow>

        <EuiFormRow
          label="Commercial Viability of the Innovation"
          helpText="Maximum of 200 words."
          fullWidth
        >
          <EuiTextArea
            name="viability"
            placeholder="Explain the innovation's ability to operate as a business and compete effectively to make a profit"
            inputRef={register({ required: true })}
            isInvalid={errors.viability && true}
            fullWidth
          />
        </EuiFormRow>

        <EuiFormRow
          label="Technical Feasibility"
          helpText="Maximum of 200 words."
          fullWidth
        >
          <EuiTextArea
            name="feasibility"
            placeholder="Explain how the innovation is designed, the processes or technologies involved, and whether a prototype exists to demonstrate its functionality. Outline the core features, materials, or methods used and provide evidence that the concept can be practically implemented, tested, and scaled"
            inputRef={register({ required: true })}
            isInvalid={errors.feasibility && true}
            fullWidth
          />
        </EuiFormRow>

        <EuiFormRow
          label="Team Composition"
          helpText="List the key members' expertise and experience to execute the project successfully."
          isInvalid={teamErrors}
          error={teamErrors ? ["Please add at least one team member."] : []}
          fullWidth
        >
          <div>
            {teamMembers.map((member, index) => (
              <div key={index}>
                <EuiFlexGroup gutterSize="s" alignItems="center">
                  <EuiFlexItem>
                    <EuiFieldText
                      placeholder="Name"
                      value={member.name}
                      onChange={(e) =>
                        updateTeamMember(index, "name", e.target.value)
                      }
                      fullWidth
                    />
                  </EuiFlexItem>
                  <EuiFlexItem>
                    <EuiFieldText
                      placeholder="Experience and Expertise"
                      value={member.expertise}
                      onChange={(e) =>
                        updateTeamMember(index, "expertise", e.target.value)
                      }
                      fullWidth
                    />
                  </EuiFlexItem>
                  {teamMembers.length > 1 && (
                    <EuiFlexItem grow={false}>
                      <EuiButtonIcon
                        iconType="trash"
                        color="danger"
                        aria-label="Remove team member"
                        onClick={() =>
                          setTeamMembers(
                            teamMembers.filter((_, i) => i !== index)
                          )
                        }
                      />
                    </EuiFlexItem>
                  )}
                </EuiFlexGroup>
                <EuiSpacer size="s" />
              </div>
            ))}
            <EuiButtonEmpty
              iconType="plusInCircle"
              size="s"
              onClick={() =>
                setTeamMembers([...teamMembers, { name: "", expertise: "" }])
              }
            >
              Add team member
            </EuiButtonEmpty>
          </div>
        </EuiFormRow>

        <EuiFormRow
          label="Cost Requirements"
          helpText="Provide items/activities required and associated costs to implement the project/solution."
          isInvalid={costErrors}
          error={costErrors ? ["Please add at least one item/activity."] : []}
          fullWidth
        >
          <div>
            {costItems.map((item, index) => (
              <div key={index}>
                <EuiFlexGroup gutterSize="s" alignItems="center">
                  <EuiFlexItem grow={2}>
                    <EuiFieldText
                      placeholder="Item/Activity Description"
                      value={item.description}
                      onChange={(e) =>
                        updateCostItem(index, "description", e.target.value)
                      }
                      fullWidth
                    />
                  </EuiFlexItem>
                  <EuiFlexItem grow={1}>
                    <EuiFieldText
                      placeholder="Cost (ZMW)"
                      value={item.cost}
                      onChange={(e) =>
                        updateCostItem(index, "cost", e.target.value)
                      }
                      fullWidth
                    />
                  </EuiFlexItem>
                  {costItems.length > 1 && (
                    <EuiFlexItem grow={false}>
                      <EuiButtonIcon
                        iconType="trash"
                        color="danger"
                        aria-label="Remove item"
                        onClick={() =>
                          setCostItems(costItems.filter((_, i) => i !== index))
                        }
                      />
                    </EuiFlexItem>
                  )}
                </EuiFlexGroup>
                <EuiSpacer size="s" />
              </div>
            ))}
            <EuiButtonEmpty
              iconType="plusInCircle"
              size="s"
              onClick={() =>
                setCostItems([...costItems, { description: "", cost: "" }])
              }
            >
              Add item/activity
            </EuiButtonEmpty>
          </div>
        </EuiFormRow>

        <EuiFormRow
          label="Supporting Image"
          helpText="Applicants are advised to attach pictures/photos of their project as evidence of the existence of the innovation. This is important for the evaluation process."
          fullWidth
        >
          <EuiFilePicker
            fullWidth
            accept="image/x-png,image/gif,image/jpeg"
            onChange={(files) => {
              onChange(files);
            }}
          />
        </EuiFormRow>

        <EuiSpacer size="l" />
        <EuiText>
          <h3>Section (D): Disclaimer</h3>
          <p>
            The information you provide in this application is used when
            processing and assessing your submission. Summary information
            provided by all applicants will be stored at NTBC and remains the
            property of the NTBC. The information you have provided, including
            personal information, may be used by the NTBC and NII partners to
            contact you or may be issued to organisations which support similar
            programmes, and may be made public through national and local media
            or via the NTBC websites when deemed necessary for funding
            purposes.
          </p>
          <p>I, the undersigned, certify that:</p>
          <ul>
            <li>
              I have read the relevant information provided for this
              application and meet the general eligibility criteria.
            </li>
            <li>
              The statements in this application are true to the best of my
              knowledge and any supporting material is my own work or the work
              of the persons named in this application.
            </li>
            <li>
              I accept that NTBC and NII partners reserve the right to use
              whatever assessment processes and selection criteria they deem
              necessary to evaluate this and other applications for funding.
            </li>
            <li>
              I acknowledge that this application will be assessed on its
              merits, and compared to other applications, and that it may be
              awarded, or it may not be awarded.
            </li>
            <li>
              If my application is successful, I consent to the media being
              given information, other than personal information, about the
              awarded project and agree that I may be contacted directly.
            </li>
            <li>
              I consent to the use of this information for other purposes
              confined to developing my innovation/idea further with the
              condition of prior notice and authorization by myself.
            </li>
          </ul>
        </EuiText>
        <EuiSpacer size="s" />
        <EuiFormRow
          isInvalid={disclaimerErrors}
          error={
            disclaimerErrors
              ? ["You must accept the declaration to submit your application."]
              : []
          }
          fullWidth
        >
          <EuiCheckbox
            id={`${idPrefix}-disclaimer`}
            label="I hereby declare that the information supplied above is true and correct to the best of my knowledge and belief."
            checked={disclaimerChecked}
            onChange={(e) => {
              setDisclaimerChecked(e.target.checked);
              setDisclaimerErrors(false);
            }}
          />
        </EuiFormRow>

        <EuiSpacer />

        <EuiButton fill type="submit" isLoading={loading}>
          Submit Innovation
        </EuiButton>
      </EuiForm>
      <div className={logosClass}>
        <img
          src={ntbc_logo}
          alt="NTBC Logo"
          style={{ width: "auto", height: 124, marginRight: 4 }}
        />
        {/* <img
          src={undp_logo}
          alt="UNDP Logo"
          style={{ width: "auto", height: 64, marginRight: 4 }}
        /> */}
      </div>
    </div>
  );
};

export const ApplicationComplete: React.FC = () => {
  const payload = {
    recipient: localStorage.getItem("recipient"),
    greeting: "0762432603",
    recipientsName: localStorage.getItem("recipientsName"),
    subject: "something",
    message: "something",
  };
  useEffect(() => {
    axios.post("https://ntbc-mailer.herokuapp.com/send-email", payload);
  }, [payload]);
  return (
    <div>
      <EuiEmptyPrompt
        iconType="editorStrike"
        title={<h2>Your Application has be Submitted</h2>}
        body={
          <Fragment>
            <p>Thank you for submitting your application.</p>
            <p>
              For further queries, call, text, or Whatsapp this number
              +260977622191
            </p>
          </Fragment>
        }
        actions={
          <EuiButton
            color="primary"
            fill
            onClick={() => (window.location.href = "https://ntbc.co.zm")}
          >
            Continue
          </EuiButton>
        }
      />
    </div>
  );
};

function App() {
  useEffect(() => {
    localStorage.clear();
  }, []);
  return (
    <Switch>
      <Route path="/" component={NIIHome} exact />
      <Route path="/apply" component={ApplicationForm} exact />
      <Route path="/submitted" component={ApplicationComplete} />
      <Route path="/admin/login" component={AdminLogin} />
      <Route path="/admin/submissions" component={ApplicationSubmissions} />
      <Route />
    </Switch>
  );
}

export default App;
